import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './Sidebar.css';
import ConversationList from '../conversationList';
import ConfirmDialog from '../confirmDialog';

const Sidebar = ({ isOpen, onClose, user, onLogin, onLogout, onDeleteAccount, onActivities, onMarketplace, onSettings, onProfile, conversations, currentConversationId, onSelectConversation, onDeleteConversation, onNewChat }) => {
  const { t } = useTranslation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const userMenuRef = useRef(null);

  const menuItems = user ? [
    { id: 1, label: t('sidebar.newChat', '新对话'), icon: 'message', onClick: onNewChat },
    { id: 2, label: t('sidebar.activities', '活动'), icon: 'apps', onClick: onActivities },
    { id: 3, label: t('sidebar.marketplace', '市场'), icon: 'store', onClick: onMarketplace },
    { id: 4, label: t('sidebar.settings', '设置'), icon: 'apps', onClick: onSettings },
    { id: 5, label: t('sidebar.profile', '我的'), icon: 'user', onClick: () => { console.log('Profile menu item clicked'); if (onProfile) onProfile(); } },
  ] : [
    { id: 3, label: t('sidebar.marketplace', '市场'), icon: 'store', onClick: onMarketplace },
    { id: 4, label: t('sidebar.settings', '设置'), icon: 'apps', onClick: onSettings },
  ];

  // Close dropdown menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleUserInfoClick = (e) => {
    e.stopPropagation();
    setShowUserMenu(!showUserMenu);
  };

  const handleLogoutClick = () => {
    if (onLogout) {
      onLogout();
    }
    setShowUserMenu(false);
  };

  const handleDeleteAccountClick = () => {
    setShowUserMenu(false);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (onDeleteAccount) {
      onDeleteAccount();
    }
    setShowDeleteConfirm(false);
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="sidebar-overlay" onClick={onClose}></div>
      )}
      
      {/* Sidebar */}
      <div className={`sidebar ${isOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <button className="sidebar-close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="sidebar-content">
          <div className="sidebar-menu">
            {menuItems.map(item => (
              <div 
                key={item.id} 
                className="sidebar-menu-item"
                onClick={item.onClick}
              >
                <span className="sidebar-menu-icon">{item.label.charAt(0)}</span>
                <span className="sidebar-menu-label">{item.label}</span>
              </div>
            ))}
          </div>
          
          {/* Conversation list */}
          {user && (
            <div className="sidebar-conversations">
              <div className="sidebar-conversations-header">{t('sidebar.conversations', '对话列表')}</div>
              <ConversationList
                conversations={conversations || []}
                currentConversationId={currentConversationId}
                onSelectConversation={onSelectConversation}
                onDeleteConversation={onDeleteConversation}
              />
            </div>
          )}
        </div>

        <div className="sidebar-footer">
          {user ? (
            <div className="sidebar-user-container" ref={userMenuRef}>
              <div className="sidebar-user-info" onClick={handleUserInfoClick}>
                <div className="sidebar-user-avatar">
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} />
                  ) : (
                    <div className="sidebar-user-avatar-placeholder">
                      {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                  )}
                </div>
                <div className="sidebar-user-name">{user.name || t('sidebar.user', '用户')}</div>
                <svg 
                  className={`sidebar-user-menu-icon ${showUserMenu ? 'open' : ''}`}
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              {showUserMenu && (
                <div className="sidebar-user-menu">
                  <button className="sidebar-user-menu-item" onClick={handleLogoutClick}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16 17 21 12 16 7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                    <span>{t('sidebar.logout', '退出登录')}</span>
                  </button>
                  <button className="sidebar-user-menu-item sidebar-user-menu-item-danger" onClick={handleDeleteAccountClick}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                    <span>{t('sidebar.deleteAccount', '删除账户')}</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="sidebar-login-btn" onClick={onLogin}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                <polyline points="10 17 15 12 10 7"></polyline>
                <line x1="15" y1="12" x2="3" y2="12"></line>
              </svg>
              <span>{t('sidebar.login', '登录')}</span>
            </button>
          )}
        </div>
      </div>
      
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title={t('confirmDialog.deleteAccountTitle')}
        message={t('confirmDialog.deleteAccountMessage')}
        confirmText={t('confirmDialog.deleteAccountConfirm')}
        cancelText={t('confirmDialog.cancel')}
        type="danger"
      />
    </>
  );
};

export default Sidebar;

